import { useEffect } from "react";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { isArray } from "lodash";
import { Button } from "@/pages/quiz/15000/components/Button";
import { Checkbox } from "@/pages/quiz/15000/components/Checkbox";
import { Textarea } from "@/pages/quiz/15000/components/Textarea";
import { getUpdatedExclusiveQuestionValue } from "@/utils/getUpdatedExclusiveQuestionValue";

type MultipleChoiceAnswer = {
  answer?: string[];
  inputValue?: Record<string, string | undefined>;
};

const MultipleChoice = ({
  question,
  questionValue,
  setQuestionAnswer,
  setQuestionValue,
}: QuizQuestionProps<MultipleChoiceAnswer>) => {
  const { options } = question.attributes;

  useEffect(() => {
    if (questionValue && isArray(questionValue)) {
      setQuestionValue({
        answer: questionValue,
      });
    }
  }, [questionValue]);

  const hasEmptyTextArea = questionValue.answer?.some((answer) => {
    const option = options?.find((option) => option.value === answer);

    if (
      option?.innerBlock &&
      !questionValue.inputValue?.[option.value]?.trim()
    ) {
      return true;
    }

    return false;
  });

  const handleCheckboxChange = (value: string) => {
    const updatedVal = getUpdatedExclusiveQuestionValue(value, questionValue);
    setQuestionValue(updatedVal);
  };

  const handleTextareaInputChange =
    (value: string) => (textareaValue: string) => {
      setQuestionValue({
        ...questionValue,
        inputValue: {
          ...(questionValue?.inputValue ?? {}),
          [value]: textareaValue,
        },
      });
    };

  return (
    <>
      <div className="flex flex-col gap-6 mt-2" key={question.id}>
        <div className="flex flex-col gap-1">
          {options?.map(({ value, label, innerBlock }, index) => {
            const innerBlockLabel = innerBlock?.label || "";
            const selected = questionValue.answer?.includes(value);

            const innerBlockValue = questionValue?.inputValue?.[value] || "";

            return (
              <Checkbox
                key={`${value}-${index}`}
                label={label}
                checked={selected}
                value={value}
                onChange={({ target }) => handleCheckboxChange(target.value)}
                className="first:rounded-t-2xl last:rounded-b-2xl"
              >
                {innerBlockLabel && selected && (
                  <div className="flex flex-col gap-2">
                    <div className="text-new-design-gray-500/50 text-sm">
                      {innerBlockLabel}
                    </div>
                    <Textarea
                      minRows={4}
                      maxRows={4}
                      autoFocus={!innerBlockValue?.trim()}
                      placeholder={innerBlock?.placeholder || "Type here..."}
                      value={innerBlockValue}
                      onChange={(e) =>
                        handleTextareaInputChange(value)(e.target.value)
                      }
                      className="w-full"
                    />
                  </div>
                )}
              </Checkbox>
            );
          })}
        </div>
      </div>

      <Button
        disabled={!questionValue?.answer?.length || hasEmptyTextArea}
        onClick={() => setQuestionAnswer(questionValue)}
      />
    </>
  );
};

export { MultipleChoice };
